import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, map, tap, switchMap} from 'rxjs/operators';

import {environment} from '@environments/environment';
import {Auth, isTool, SKIP_AUTHENTICATION, skipAuthentication, skipModalError} from '../_models/auth';
import {authLocalEmail, authLocalKey} from '@config/config';
import {User} from '@app/shared/_interfaces/user';
import {CookieService} from 'ngx-cookie-service';
import {Authentication} from '@app/shared/_interfaces/auth';
import {UserService} from './user.service';
import {Myaccount} from '@app/features/myaccount/myaccount.interface';

@Injectable({providedIn: 'root'})
export class AuthenticationService {
  private http = inject(HttpClient);
  private cookieService = inject(CookieService);
  private userService = inject(UserService);


  private currentUserSubject: BehaviorSubject<Auth>;
  public currentUser: Observable<Auth>;

  private authorizeUrl = `${environment.newApiUrl}/authorize`;
  private deauthorizeUrl = `${environment.newApiUrl}/deauthorize`;
  private forgotPasswordUrl = `${environment.newApiUrl}/forgot-password`;
  private changePasswordUrl = `${environment.newApiUrl}/change-password`;
  private authorizeGoogleUrl = `${environment.newApiUrl}/google-authorize`;
  private userInfoUrl = `${environment.newApiUrl}/user/config`;

  constructor() {
    this.currentUserSubject = new BehaviorSubject<Auth>({
      authkey: localStorage.getItem(authLocalKey),
      authemail: localStorage.getItem(authLocalEmail)
    });
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): Auth {
    return this.currentUserSubject.value;
  }

  toggleCookie(userInfo?: User.Info){

    this.cookieService.delete('TurboSESS', '/', environment.cookieDomain, false);

    const isExecutiveAccount = userInfo?.is_executive_account;
    if (isExecutiveAccount){
      this.cookieService.set('isExecutiveAccount', 'true', 365, '/', environment.cookieDomain, false);
    } else {
      this.cookieService.delete('isExecutiveAccount', '/', environment.cookieDomain, false);
    }
  }

  isLogged(): boolean {
    const user: Auth = this.currentUserValue;
    if (!user || !user.authkey || !user.authemail) {
      return false;
    }
    return user.authemail !== '' && user.authkey !== '';
  }

  isLoggedAsync(): Observable<boolean> {
    const user: Auth = this.currentUserValue;

    // If user info already loaded, avoid another /user/config call
    const cachedUserInfo = this.userService.getValue();
    if (cachedUserInfo) {
      return of(true);
    }

    return this.userService.getUserInfo().pipe(
      map((_userInfo: User.Info) => true),
      catchError((error) => {
        // Only treat 401/403 as unauthenticated; allow 404/others to pass
        if (error?.status === 401 || error?.status === 403) {
          return of(false);
        }
        return of(true);
      })
    );
  }

  public createCookieDetails(authemail: string, authkey: string, cb?: Function) {
    this.currentUserSubject.next({
      authemail,
      authkey
    });
    localStorage.setItem(authLocalKey, authkey);
    localStorage.setItem(authLocalEmail, authemail)
    cb && cb();
  }

  login(email: string, password: string, noExpire?: boolean) {
    return this.http.post<{ auth: string }>(`${this.authorizeUrl}`, {email, password, no_expire: !!noExpire}, {
      context: skipAuthentication()
    }).pipe(map((user: { auth: string }) => {
      if (user && user.auth) {
        this.createCookieDetails(email, user.auth);
      }
      return user;
    }));
  }

  deauthorizeToken() {
    localStorage.removeItem(authLocalKey);
    localStorage.removeItem(authLocalEmail);
    this.currentUserSubject.next({ authkey: null, authemail: null });
    this.cookieService.delete('isExecutiveAccount', '/', environment.cookieDomain, false);
  }

  logout() {
    return this.http.post<Authentication.LogoutResponse>(
      this.deauthorizeUrl, {}, {
        context: skipModalError([401])
      }
    ).pipe(
      catchError(err => {
        return of({message: err?.error?.message})
      }),
      tap(_ => {
        if(this.cookieService.get('targetAccountId')) {
          this.cookieService.delete('targetAccountId', '/', environment.cookieDomain, false);
          window.location.href = '/';
        }else {
          this.deauthorizeToken();
          window.location.href = environment.logoutUrl;
        }
      })
    );
  }

  sendTokenForgotPassword(email: string) {
    return this.http.post<{ message: string }>(
      this.forgotPasswordUrl, {email}, {
        context: skipModalError(['all']).set(SKIP_AUTHENTICATION, true)
      }
    );
  }

  changePasswordForgotten(token: string, password: string, confirm_password: string) {
    return this.http.put<{ message: string }>(
      this.forgotPasswordUrl, {token, password, confirm_password}, {
        context: skipModalError(['all'])
          .set(SKIP_AUTHENTICATION, true)
      }
    );
  }

  changePassword(current_password: string, password: string, confirm_password: string) {
    return this.http.put<{ message: string }>(
      this.changePasswordUrl, {current_password, password, confirm_password},
      {
        context: isTool('change_password')
      }
    );
  }

  isTokenForgotPasswordValid(token: string) {
    return this.http.get<{ message: string }>(
      this.forgotPasswordUrl, {
        params: {token},
        context: skipModalError(['all'])
          .set(SKIP_AUTHENTICATION, true)
      }
    );
  }

  loginWithGoogle(response: any) {
    console.log('loginWithGoogle', response);
      return this.http.post<{ auth: string, email: string }>(`${this.authorizeGoogleUrl}`, response, {
        context: skipAuthentication()
      }).pipe(map((user: { auth: string, email: string }) => {
        if (user && user.auth && user.email) {
          this.createCookieDetails(user.email, user.auth);
        }
        return user;
      }));
    }

  // MFA / 2FA methods
  setMfaMethod(keycloakUserId: string, mfaMethod: Myaccount.MfaMethod): Observable<Myaccount.MfaMethodResponse> {
    return this.http.put<Myaccount.MfaMethodResponse>(
      `${environment.newApiUrl}/keycloak/users/${keycloakUserId}/mfa-method`,
      { mfa_method: mfaMethod }
    );
  }

  generateTotpSecret(keycloakUserId: string): Observable<Myaccount.TotpGenerateResponse> {
    return this.http.get<Myaccount.TotpGenerateResponse>(
      `${environment.newApiUrl}/keycloak/users/${keycloakUserId}/totp/generate`
    );
  }

  registerTotpCredential(keycloakUserId: string, data: Myaccount.TotpRegisterRequest): Observable<Myaccount.TotpRegisterResponse> {
    return this.http.post<Myaccount.TotpRegisterResponse>(
      `${environment.newApiUrl}/keycloak/users/${keycloakUserId}/totp/register`,
      data
    );
  }
}
